/* eslint-disable */
import React, { useMemo } from 'react';
import PRPie from '../PracticeReport/PRPie';
import { parseMoney } from '../../utils/index';

const FirmReportNaicsAnalysis = ({
  naics_averages,
  pieTopLevelArr,
  child_count,
  drillup,
  drillup_text,
  setSelectedFilters,
  selectedFilters,
  handleSubmit,
  sortConfig,
  onSortChange
}) => {

  const compareNumber = (a, b, direction = 1) => {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return (a > b ? 1 : -1) * direction;
  }

  const compareText = (a, b) => {
    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  }

  const normalizeNaicsValue = (naics) => {
    if (naics === 'ALL') {
      return Number.NEGATIVE_INFINITY;
    }
    const parsed = parseInt(naics, 10);
    return Number.isFinite(parsed) ? parsed : naics;
  }

  const sortedData = useMemo(() => {
    const allRows = Array.isArray(naics_averages?.ALL) ? [...naics_averages.ALL] : [];
    const mainRows = [];

    if (naics_averages) {
      Object.entries(naics_averages).forEach(([naics, rows]) => {
        if (naics === 'ALL') return;
        if (Array.isArray(rows)) {
          rows.forEach(row => {
            if (row) {
              mainRows.push(row);
            }
          });
        }
      });
    }

    const direction = sortConfig?.isDesc ? -1 : 1;
    const compareNaics = (a, b) => {
      const aValue = normalizeNaicsValue(a.naics);
      const bValue = normalizeNaicsValue(b.naics);
      if (aValue === bValue) return 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue > bValue ? 1 : -1;
      }
      return String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
    }

    const compareRows = (a, b) => {
      const sortBy = sortConfig?.sortBy || 'naics';
      const compareValues = (key) => {
        if (key === 'naics') {
          return compareNaics(b, a) * direction;
        }
        if (key === 'year') {
          return compareNumber(a.year, b.year, direction);
        }
        return compareNumber(a[key], b[key], direction);
      }

      const primary = compareValues(sortBy);
      if (primary !== 0) {
        return primary;
      }

      const naicsTie = compareNaics(b, a) * -1;
      if (naicsTie !== 0) {
        return naicsTie;
      }
      return compareNumber(b.year, a.year);
    }

    mainRows.sort(compareRows);
    allRows.sort((a, b) => compareNumber(b.year, a.year));

    return { mainRows, allRows };
  }, [naics_averages, sortConfig]);

  const renderTableCell = (value, formatter, append) => {
    if (Number.isFinite(value)) {
      const formatted = formatter ? formatter(value) : value;
      return append ? `${formatted}${append}` : formatted;
    }
    return '-';
  }

  const renderHeaderCell = (key, label, isRightBorder = false) => {
    const isActiveSort = sortConfig?.sortBy === key;
    const directionSymbol = isActiveSort ? (sortConfig.isDesc ? ' ↓' : ' ↑') : '';
    const cellContent = (
      <span>
        {label}{directionSymbol}
      </span>
    );

    return (
      <td
        title="Click to Sort"
        className='sort-table-col'
        onClick={() => onSortChange(key)}
        style={isRightBorder ? { borderRight: '1px solid #d7e4cf', cursor: 'pointer' } : { cursor: 'pointer' }}
      >
        {cellContent}
      </td>
    );
  }

  const renderRow = (el, index, rowStyle = {}) => {
    return (
      <tr key={`${el.naics}-${el.year}-${index}`} style={rowStyle}>
        <td>{el.year || '-'}</td>
        <td>{el.naics || 'N/A'}</td>
        <td>{Number.isFinite(el.count) ? el.count : '0'}</td>
        <td style={{ borderRight: '1px solid #d7e4cf' }}>{Number.isFinite(el.submitted) ? el.submitted : '0'}</td>
        <td>{renderTableCell(el.total_revenue, parseMoney)}</td>
        <td>{renderTableCell(el.total_revenue_growth_rate, (value) => (value * 100).toFixed(2), '%')}</td>
        <td>{renderTableCell(el.total_costs_common_size_revenue, (value) => (value * 100).toFixed(2), '%')}</td>
        <td>{renderTableCell(el.total_operating_expenses_common_size_revenue, (value) => (value * 100).toFixed(2), '%')}</td>
        <td>{renderTableCell(el.operating_expenses_net_income_common_size_revenue, (value) => (value * 100).toFixed(2), '%')}</td>
        <td>{renderTableCell(el.growth, (value) => value.toFixed(0))}</td>
        <td>{renderTableCell(el.profitability, (value) => value.toFixed(0))}</td>
        <td>{renderTableCell(el.liquidity, (value) => value.toFixed(0))}</td>
        <td>{renderTableCell(el.leverage, (value) => value.toFixed(0))}</td>
        <td>{renderTableCell(el.cash_flow, (value) => value.toFixed(0))}</td>
      </tr>
    )
  }

  return (
    <div className='sheet-wrapper report-card naics-analysis-container practice-report-pdf'>
      <PRPie
        pieTopLevelArr={pieTopLevelArr}
        child_count={child_count}
        drillup={drillup}
        drillup_text={drillup_text}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
        handleSubmit={handleSubmit}
      />
      <div className="page-break" />
      <div className='naics-analysis-table-container'>
        <h1 className="pr-table-title hide-not-on-print">NAICS Analysis - Firm Report</h1>
        <table id='naics-analysis-data' className='group-table-pr'>
          <thead className='group-header-pr'>
            <tr className='practice-header'>
              {renderHeaderCell('year', 'Year')}
              {renderHeaderCell('naics', 'NAICS')}
              {renderHeaderCell('count', 'Total Count')}
              {renderHeaderCell('submitted', 'Submitted Count', true)}
              {renderHeaderCell('total_revenue', 'Total Revenue')}
              {renderHeaderCell('total_revenue_growth_rate', 'Growth%')}
              {renderHeaderCell('total_costs_common_size_revenue', 'COGS')}
              {renderHeaderCell('total_operating_expenses_common_size_revenue', 'Total OPEX')}
              {renderHeaderCell('operating_expenses_net_income_common_size_revenue', 'Net Income', true)}
              {renderHeaderCell('growth', 'Growth')}
              {renderHeaderCell('profitability', 'Profitability')}
              {renderHeaderCell('liquidity', 'Liquidity')}
              {renderHeaderCell('leverage', 'Leverage')}
              {renderHeaderCell('cash_flow', 'Cash Flow')}
            </tr>
          </thead>
          <tbody>
            {sortedData.mainRows.map((el, index) => renderRow(el, index))}
            {sortedData.allRows.map((el, index) => renderRow(el, index, { border: '2px dashed #69b144' }))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default FirmReportNaicsAnalysis;
